import React, { useState } from 'react';
import IntakeFormLayout from '../components/intake-form/IntakeFormLayout';
import ClientInformation from '../components/intake-form/steps/ClientInformation';
import SpouseAndFamily from '../components/intake-form/steps/SpouseAndFamily';
import ExistingEstateDocuments from '../components/intake-form/steps/ExistingEstateDocuments';
import Beneficiaries from '../components/intake-form/steps/Beneficiaries';
import SpecialNeeds from '../components/intake-form/steps/SpecialNeeds';
import AssetsAndInsurance from '../components/intake-form/steps/AssetsAndInsurance';
import FinancialPOA from '../components/intake-form/steps/FinancialPOA';
import HealthcarePOA from '../components/intake-form/steps/HealthcarePOA';
import FormSummary from '../components/intake-form/FormSummary';
import ConfirmationPage from '../components/intake-form/ConfirmationPage';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Define the form schema using Zod
const formSchema = z.object({
  clientInfo: z.object({
    existingClient: z.boolean().optional(),
    referralSource: z.string().optional(),
    fullName: z.string().min(1, 'Full name is required'),
    age: z.string().min(1, 'Age is required'),
    maritalStatus: z.string().min(1, 'Marital status is required'),
    address: z.string().min(1, 'Address is required'),
    phone: z.string().min(1, 'Phone number is required'),
    email: z.string().email('Invalid email address'),
    occupation: z.string().optional(),
    employerName: z.string().optional(),
    businessAddress: z.string().optional(),
    businessPhone: z.string().optional(),
    citizenship: z.string().optional()
  })
});
type FormData = z.infer<typeof formSchema>;
const IntakeForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      clientInfo: {
        existingClient: false,
        referralSource: '',
        fullName: '',
        age: '',
        maritalStatus: '',
        address: '',
        phone: '',
        email: '',
        occupation: '',
        employerName: '',
        businessAddress: '',
        businessPhone: '',
        citizenship: ''
      }
    },
    mode: 'onChange'
  });
  const steps = [{
    id: 'client-info',
    title: 'Client Information',
    component: <ClientInformation data-id="element-718" />
  }, {
    id: 'spouse-family',
    title: 'Spouse & Family',
    component: <SpouseAndFamily data-id="element-719" />
  }, {
    id: 'estate-docs',
    title: 'Existing Estate Documents',
    component: <ExistingEstateDocuments data-id="element-720" />
  }, {
    id: 'beneficiaries',
    title: 'Beneficiaries',
    component: <Beneficiaries data-id="element-721" />
  }, {
    id: 'special-needs',
    title: 'Corporate, Dependents & Special Needs',
    component: <SpecialNeeds data-id="element-722" />
  }, {
    id: 'assets-insurance',
    title: 'Assets & Insurance',
    component: <AssetsAndInsurance data-id="element-723" />
  }, {
    id: 'financial-poa',
    title: 'Power of Attorney: Financial',
    component: <FinancialPOA data-id="element-724" />
  }, {
    id: 'healthcare-poa',
    title: 'Power of Attorney: Healthcare',
    component: <HealthcarePOA data-id="element-725" />
  }, {
    id: 'summary',
    title: 'Review & Confirm',
    component: <FormSummary onEditStep={stepIndex => setCurrentStep(stepIndex)} data-id="element-726" />
  }];
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  const handleSubmit = methods.handleSubmit(data => {
    console.log('Form submitted:', data);
    setIsSubmitted(true);
  });
  if (isSubmitted) {
    return <ConfirmationPage data-id="element-727" />;
  }
  return <FormProvider {...methods} data-id="element-728">
      <IntakeFormLayout currentStep={currentStep} totalSteps={steps.length} title={steps[currentStep].title} onNext={currentStep === steps.length - 1 ? handleSubmit : handleNext} onBack={handleBack} isFirstStep={currentStep === 0} isLastStep={currentStep === steps.length - 1} steps={steps} data-id="element-729">
        {steps[currentStep].component}
      </IntakeFormLayout>
    </FormProvider>;
};
export default IntakeForm;